import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ShieldCheck, Headset, UserCircle, Bank } from "@phosphor-icons/react";
import { useT } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { LOGO_URL } from "../components/Reveal";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1570133435807-5feefeb2d196?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const QUICK = [
  { role: "admin", label: "Admin", email: "admin@yadimmo.com", password: "Yadimmo2026!", icon: ShieldCheck },
  { role: "service", label: "Service client", email: "service@yadimmo.com", password: "Service2026!", icon: Headset },
  { role: "investor", label: "Investisseur", email: "investisseur@yadimmo.com", password: "Invest2026!", icon: UserCircle },
  { role: "bank", label: "Banque", email: "banque@yadimmo.com", password: "Banque2026!", icon: Bank },
];

const Login = () => {
  const { t } = useT();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = await login(form.email, form.password);
      toast.success(t("auth.login_success"));
      navigate(u.role === "admin" ? "/admin" : "/espace");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const quickLogin = async (q) => {
    setError(""); setLoading(true);
    try {
      const u = await login(q.email, q.password);
      toast.success(t("auth.login_success"));
      navigate(u.role === "admin" ? "/admin" : "/espace");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 relative z-10">
      <div className="hidden lg:block relative">
        <img src={HERO} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-navy/60" />
        <div className="absolute bottom-12 start-12 text-white max-w-sm">
          <div className="font-display text-3xl">Bâtir & Grandir</div>
          <p className="text-white/70 mt-2">{t("hero.subtitle")}</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <div className="w-full max-w-md">
          <Link to="/"><img src={LOGO_URL} alt="YADIMMO" className="h-9 mb-10" /></Link>
          <h1 className="font-display text-3xl text-navy font-light mb-8">{t("auth.login_title")}</h1>

          {error && <div data-testid="login-error" className="mb-4 text-sm text-destructive bg-destructive/10 border border-destructive/20 px-4 py-3">{error}</div>}

          <form data-testid="login-form" onSubmit={submit} className="space-y-4">
            <Field label={t("auth.email")}><Input data-testid="login-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-12 rounded-lg border-navy/15" /></Field>
            <Field label={t("auth.password")}><Input data-testid="login-password" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-12 rounded-lg border-navy/15" /></Field>
            <button data-testid="login-submit" disabled={loading} className="w-full bg-navy text-white font-semibold py-4 rounded-full hover:bg-[#254B75] transition-colors disabled:opacity-60">
              {loading ? t("common.loading") : t("auth.login_btn")}
            </button>
          </form>

          <p className="mt-6 text-sm text-muted-foreground">
            {t("auth.no_account")} <Link to="/inscription" data-testid="link-register" className="text-navy font-semibold hover:text-gold">{t("nav.register")}</Link>
          </p>

          <div className="mt-8 pt-6 border-t border-navy/10">
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Connexion rapide (démo)</div>
            <div className="grid grid-cols-2 gap-2">
              {QUICK.map((q) => (
                <button
                  key={q.role}
                  data-testid={`quick-login-${q.role}`}
                  disabled={loading}
                  onClick={() => quickLogin(q)}
                  className="text-start border border-navy/15 rounded-xl px-3 py-2.5 hover:border-gold hover:bg-gold/5 transition-colors"
                >
                  <div className="text-sm font-semibold text-navy flex items-center gap-1.5"><q.icon size={16} weight="light" className="text-gold" /> {q.label}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{q.email}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const Field = ({ label, children }) => (
  <div>
    <label className="text-sm font-medium text-navy block mb-2">{label}</label>
    {children}
  </div>
);

export default Login;
